import { httpClient } from '../http-client';
import { buildQueryString } from './query-builder';
import { ApiResponse } from '../../types/responses/api-response';
import type {
  ParticipantEventResponseDto,
  ParticipantEventsResponseDto,
  ParticipantHistoryEventResponseDto,
  ParticipantHistoryResponseDto,
  ParticipantRecommendationResponseDto,
  ParticipantRecommendationsResponseDto,
} from '../../api/generated/types.gen';

// ===== PARTICIPANT API TYPES =====

/**
 * AD-415. ТРИ ТИПА НИЖЕ — ПСЕВДОНИМЫ ГЕНЕРАТА, А НЕ СОБСТВЕННЫЕ ОБЪЯВЛЕНИЯ.
 *
 * До этой правки они были рукописными и расходились с ответом сервера
 * ИМЕНАМИ ПОЛЕЙ, а не мелочами: `date` там, где сервер шлёт `startDate`,
 * `category` там, где `eventType`, `title/status/role` там, где
 * `eventTitle/participationStatus/participantRole`. Компилятор такое
 * расхождение поймать не мог — он сверял экран с объявлением, а объявление
 * ни с чем не сверялось. На экране это выглядело как «Invalid Date» в
 * строках двух вкладок и падение третьей на `undefined`.
 *
 * Псевдоним закрывает класс целиком: генерат пересобирается из `openapi.json`
 * в том же проходе (гейт IMG-6), и любое будущее расхождение имён становится
 * ошибкой типов у КАЖДОГО читателя — на всех экранах кабинета сразу, а не на
 * том, где кто-то заметил глазами.
 */
export type ParticipantEvent = ParticipantEventResponseDto;

export type ParticipantRecommendation = ParticipantRecommendationResponseDto;

export type ParticipantHistory = ParticipantHistoryEventResponseDto;

/**
 * AD-415. Мобильная панель: `GET /participant/mobile/dashboard`.
 *
 * Прежний тип обещал `{upcomingEvents, recommendations, stats, notifications}`,
 * а сервер отдаёт `{recommendations, upcomingEvents, recentHistory, quickStats}`.
 * Из четырёх ключей совпадали два: экран, написанный по этому типу, читал бы
 * `stats` и `notifications` как `undefined`. Своего DTO у мобильной панели на
 * бэкенде нет (форма объявлена inline в контроллере), поэтому здесь она
 * собрана из тех же элементов генерата — имена элементов контракт проверяет.
 */
export interface ParticipantMobileDashboard {
  recommendations: ParticipantRecommendation[];
  upcomingEvents: ParticipantEvent[];
  recentHistory: ParticipantHistory[];
  quickStats: {
    totalEvents: number;
    upcomingEvents: number;
    completedEvents: number;
    averageRating: number;
  };
}

/**
 * AD-415. Быстрые списки мобильного приложения отдают КОНВЕРТ
 * `{success, events|recommendations, message}`, а не голый массив — прежний
 * тип `ApiResponse<ParticipantEvent[]>` обещал массив, и первый же `.map` по
 * нему упал бы. Состав строки урезан контроллером, поэтому `Pick` — он
 * заодно держит имена полей привязанными к контракту.
 */
export type ParticipantQuickEvent = Pick<
  ParticipantEvent,
  'id' | 'title' | 'startDate' | 'location' | 'price' | 'isRegistered'
> &
  Pick<Partial<ParticipantEvent>, 'imageUrl'>;

export interface ParticipantQuickEventsResponse {
  success: boolean;
  events: ParticipantQuickEvent[];
  message: string;
}

/**
 * AD-419 / решение Сергея B4 (22.09.2026): 'reason' и 'score' сняты с
 * контракта сервера, поэтому выбирать их здесь больше неоткуда — Pick по
 * несуществующему ключу был бы ошибкой типов. Имя типа и путь маршрута
 * оставлены прежними намеренно (см. BE participant.controller.ts).
 */
export type ParticipantQuickRecommendation = Pick<
  ParticipantRecommendation,
  'eventId' | 'title' | 'startDate' | 'price'
> &
  Pick<Partial<ParticipantRecommendation>, 'imageUrl'>;

export interface ParticipantQuickRecommendationsResponse {
  success: boolean;
  recommendations: ParticipantQuickRecommendation[];
  message: string;
}

export interface ParticipantProfile {
  id: string;
  userId: string;
  interests: string[];
  preferences: Record<string, any>;
  eventsAttended: number;
  totalHours: number;
}

export interface ParticipantNotification {
  id: string;
  title: string;
  message: string;
  type: string;
  eventId: string;
  eventTitle: string;
  read: boolean;
  createdAt: string;
}

export interface ParticipantContent {
  id: string;
  type: string;
  content: string;
  eventId: string;
  status: string;
  createdAt: string;
}

export interface EventStats {
  eventsAttended: number;
  totalHours: number;
  favoriteCategories: string[];
  averageRating: number;
}

// KPI-агрегаты участника по всем событиям (эндпоинт GET /participant/metrics).
// Форма 1:1 c BE ParticipantMetricsDto.
export interface ParticipantMetrics {
  myEvents: number;
  attended: number;
  upcoming: number;
  contacts: number;
  notifications: number;
  averageRating: number;
  calculatedAt: string;
}

export interface ParticipantSettings {
  id: string;
  userId: string;
  emailNotifications: boolean;
  pushNotifications: boolean;
  smsNotifications: boolean;
  language: string;
  timezone: string;
}

// Query параметры
export interface UpcomingEventsQuery {
  page?: number;
  limit?: number;
  category?: string;
  format?: string;
  search?: string;
}

export interface RecommendationsQuery {
  page?: number;
  limit?: number;
  category?: string;
}

export interface HistoryQuery {
  page?: number;
  limit?: number;
  startDate?: string;
  endDate?: string;
  status?: string;
}

export interface RegisterEventData {
  dietary?: string[];
  specialRequests?: string;
  emergencyContact?: string;
}

export interface ReportContentData {
  reason: string;
  description: string;
}

// ===== PARTICIPANT API METHODS =====

export const participantApi = {
  // ==================== ОСНОВНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить предстоящие мероприятия
   */
  getUpcomingEvents: async (
    query?: UpcomingEventsQuery,
  ): Promise<ApiResponse<ParticipantEventsResponseDto>> => {
    return httpClient.get(`/participant/events/upcoming${buildQueryString(query || {})}`);
  },

  /**
   * Получить рекомендации мероприятий
   */
  getRecommendations: async (
    query?: RecommendationsQuery,
  ): Promise<ApiResponse<ParticipantRecommendationsResponseDto>> => {
    return httpClient.get(`/participant/recommendations${buildQueryString(query || {})}`);
  },

  /**
   * Получить историю участия
   */
  getHistory: async (
    query?: HistoryQuery,
  ): Promise<ApiResponse<ParticipantHistoryResponseDto>> => {
    return httpClient.get(`/participant/history${buildQueryString(query || {})}`);
  },

  /**
   * Получить профиль участника
   */
  getProfile: async (): Promise<ApiResponse<ParticipantProfile>> => {
    return httpClient.get<ParticipantProfile>('/participant/profile');
  },

  /**
   * Зарегистрироваться на событие
   */
  registerForEvent: async (eventId: string, data?: RegisterEventData): Promise<ApiResponse<{
    success: boolean;
    message: string;
    participantId: string;
  }>> => {
    return httpClient.post(`/participant/events/${eventId}/register`, data || {});
  },

  // ==================== МЕТРИКИ / KPI ====================

  /**
   * Получить KPI-метрики участника для дашборда (реальные агрегаты по всем событиям)
   */
  getMetrics: async (): Promise<ApiResponse<ParticipantMetrics>> => {
    return httpClient.get<ParticipantMetrics>('/participant/metrics');
  },

  // ==================== УВЕДОМЛЕНИЯ ====================

  /**
   * Получить уведомления
   */
  getNotifications: async (query?: { page?: number; limit?: number; read?: boolean }): Promise<ApiResponse<{
    notifications: ParticipantNotification[];
    total: number;
    unreadCount: number;
  }>> => {
    return httpClient.get(`/participant/notifications${buildQueryString(query || {})}`);
  },

  /**
   * Отметить уведомление как прочитанное
   */
  markNotificationAsRead: async (notificationId: string): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.put(`/participant/notifications/${notificationId}/read`, {});
  },

  // ==================== МОДЕРАЦИЯ ====================

  /**
   * Получить свой контент
   */
  getMyContent: async (query?: { page?: number; limit?: number }): Promise<ApiResponse<{
    content: ParticipantContent[];
    total: number;
  }>> => {
    return httpClient.get(`/participant/moderation${buildQueryString(query || {})}`);
  },

  /**
   * Пожаловаться на контент
   */
  reportContent: async (contentId: string, data: ReportContentData): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.post('/participant/moderation/report', { contentId, ...data });
  },

  // ==================== ОТЧЕТЫ ====================

  /**
   * Создать отчет участника
   */
  createReport: async (eventId: string, format: 'pdf' | 'excel' = 'pdf'): Promise<ApiResponse<{
    reportId: string;
    status: string;
  }>> => {
    return httpClient.post(`/participant/reports${buildQueryString({ eventId, format })}`);
  },

  /**
   * Получить статистику участия
   */
  getEventStats: async (): Promise<ApiResponse<EventStats>> => {
    return httpClient.get<EventStats>('/participant/reports/event-stats');
  },

  // ==================== НАСТРОЙКИ ====================

  /**
   * Получить настройки участника
   */
  getSettings: async (): Promise<ApiResponse<ParticipantSettings>> => {
    return httpClient.get<ParticipantSettings>('/participant/settings');
  },

  /**
   * Обновить настройки участника
   */
  updateSettings: async (data: Partial<ParticipantSettings>): Promise<ApiResponse<{
    success: boolean;
    message: string;
  }>> => {
    return httpClient.put('/participant/settings', data);
  },

  // ==================== МОБИЛЬНЫЕ ЭНДПОИНТЫ ====================

  /**
   * Получить мобильную панель
   */
  getMobileDashboard: async (): Promise<
    ApiResponse<ParticipantMobileDashboard>
  > => {
    return httpClient.get('/participant/mobile/dashboard');
  },

  /**
   * Получить быстрый список событий
   */
  getQuickEvents: async (
    limit: number = 5,
  ): Promise<ApiResponse<ParticipantQuickEventsResponse>> => {
    return httpClient.get<ParticipantQuickEventsResponse>(
      `/participant/mobile/events/quick${buildQueryString({ limit })}`,
    );
  },

  /**
   * Получить быстрые рекомендации
   */
  getQuickRecommendations: async (
    limit: number = 5,
  ): Promise<ApiResponse<ParticipantQuickRecommendationsResponse>> => {
    return httpClient.get<ParticipantQuickRecommendationsResponse>(
      `/participant/mobile/recommendations/quick${buildQueryString({ limit })}`,
    );
  },
};

export default participantApi;

